/**
 * Sentry event scrubber. Pure functions consumed by Sentry.init's
 * `beforeSend` and `beforeBreadcrumb` hooks to redact tokens, auth
 * headers, and sensitive query params before any event leaves the SPA.
 */

const SENSITIVE_QUERY_PARAMS = [
    'token',
    'access_token',
    'refresh_token',
    'id_token',
    'idToken',
    'firebase_id',
    'firebaseId',
    'password',
    'code',
    'secret',
    'api_key',
    'apiKey',
    'session',
    'auth',
];

const SENSITIVE_HEADERS = [
    'authorization',
    'cookie',
    'set-cookie',
    'x-refresh-token',
    'x-id-token',
];

const REDACTED = '[REDACTED]';

const SENSITIVE_PARAM_SET = new Set(SENSITIVE_QUERY_PARAMS.map((p) => p.toLowerCase()));
const SENSITIVE_HEADER_SET = new Set(SENSITIVE_HEADERS);
const SENSITIVE_KEY_SET = new Set(
    [...SENSITIVE_QUERY_PARAMS, ...SENSITIVE_HEADERS]
        .map((p) => p.toLowerCase())
);
const SENSITIVE_COMPACT_KEY_SET = new Set(
    [...SENSITIVE_QUERY_PARAMS, ...SENSITIVE_HEADERS, 'current_password', 'new_password', 'old_password', 'password_confirmation', 'client_secret', 'session_id']
        .map((p) => p.toLowerCase().replace(/[^a-z0-9]/g, ''))
);
const SENSITIVE_ASSIGNMENT_RE = /\b(token|access[_-]?token|refresh[_-]?token|id[_-]?token|firebase[_-]?id|password|current[_-]?password|new[_-]?password|old[_-]?password|password[_-]?confirmation|code|secret|client[_-]?secret|api[_-]?key|authorization|auth|set[-_]?cookie|cookie|x-refresh-token|x-id-token|session|session[_-]?id|credential)\s*([:=])\s*("[^"]*"|'[^']*'|(?:Bearer\s+)?[^\s&,;]+)/gi;

const isSensitiveKey = (key) => {
    if (typeof key !== 'string') return false;
    const lower = key.toLowerCase();
    if (SENSITIVE_KEY_SET.has(lower)) return true;
    const compact = lower.replace(/[^a-z0-9]/g, '');
    return (
        SENSITIVE_COMPACT_KEY_SET.has(compact) ||
        compact.endsWith('token') ||
        compact.includes('password') ||
        compact.includes('secret') ||
        compact.includes('credential') ||
        compact === 'authorization' ||
        compact === 'cookie' ||
        compact === 'apikey' ||
        compact === 'sessionid'
    );
};

const scrubSensitiveString = (value) => {
    if (typeof value !== 'string') return value;
    return scrubUrl(value).replace(
        SENSITIVE_ASSIGNMENT_RE,
        (match, key, separator) => `${key}${separator}${separator === ':' ? ' ' : ''}${REDACTED}`
    );
};

// Rewrite a `key=value&key=value` query string, replacing values whose key
// matches a sensitive name (case-insensitive) with the literal [REDACTED].
// Avoids URLSearchParams.toString() so the output is human-readable
// (`token=[REDACTED]`) rather than percent-encoded (`token=%5BREDACTED%5D`).
const redactQueryString = (queryStr) => {
    if (!queryStr) return queryStr;
    return queryStr
        .split('&')
        .map((pair) => {
            const eqIdx = pair.indexOf('=');
            if (eqIdx === -1) return pair;
            const key = pair.slice(0, eqIdx);
            if (SENSITIVE_PARAM_SET.has(key.toLowerCase())) {
                return `${key}=${REDACTED}`;
            }
            return pair;
        })
        .join('&');
};

/**
 * Replace every sensitive query-param value in a URL string with [REDACTED].
 * Handles absolute URLs and relative SPA paths (`/foo?token=…`).
 * Returns the input unchanged when no query string is present.
 */
export const scrubUrl = (url) => {
    if (typeof url !== 'string' || url.indexOf('?') === -1) {
        return url;
    }

    const qIdx = url.indexOf('?');
    const head = url.slice(0, qIdx);
    const queryAndHash = url.slice(qIdx + 1);
    const hashIdx = queryAndHash.indexOf('#');
    const queryStr = hashIdx === -1 ? queryAndHash : queryAndHash.slice(0, hashIdx);
    const hashStr = hashIdx === -1 ? '' : queryAndHash.slice(hashIdx);
    const redacted = redactQueryString(queryStr);
    return `${head}${redacted ? `?${redacted}` : ''}${hashStr}`;
};

/**
 * Mutate a headers object in place, replacing values of any sensitive
 * header name (case-insensitive) with [REDACTED]. No-op for non-objects.
 */
export const scrubHeaders = (headers) => {
    if (!headers || typeof headers !== 'object') return headers;
    for (const key of Object.keys(headers)) {
        if (SENSITIVE_HEADER_SET.has(key.toLowerCase())) {
            headers[key] = REDACTED;
        }
    }
    return headers;
};

/**
 * Return a deep-scrubbed clone of an arbitrary telemetry payload. Values under
 * sensitive-looking keys are replaced outright; strings elsewhere still have
 * sensitive query params and key=value fragments redacted.
 */
export const scrubSensitiveData = (value, seen = new WeakSet(), parentKey = '') => {
    if (isSensitiveKey(parentKey)) return REDACTED;
    if (typeof value === 'string') return scrubSensitiveString(value);
    if (value === null || value === undefined || typeof value !== 'object') return value;
    if (value instanceof Date) return value;

    if (seen.has(value)) return '[Circular]';
    seen.add(value);

    if (Array.isArray(value)) {
        return value.map((item) => scrubSensitiveData(item, seen));
    }

    const out = {};
    for (const [key, child] of Object.entries(value)) {
        out[key] = scrubSensitiveData(child, seen, key);
    }
    return out;
};

const scrubBreadcrumb = (breadcrumb) => {
    if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;

    if (breadcrumb.category === 'console' && typeof breadcrumb.message === 'string') {
        const lower = breadcrumb.message.toLowerCase();
        for (const param of SENSITIVE_PARAM_SET) {
            if (lower.includes(param)) return null;
        }
    }

    if (breadcrumb.data && typeof breadcrumb.data === 'object') {
        if (typeof breadcrumb.data.url === 'string') {
            breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
        }
        if (breadcrumb.data.request_headers) {
            scrubHeaders(breadcrumb.data.request_headers);
        }
        if (breadcrumb.data.response_headers) {
            scrubHeaders(breadcrumb.data.response_headers);
        }
    }

    return breadcrumb;
};

/**
 * Sentry `beforeSend` hook. Always returns the (possibly mutated) event so
 * legitimate errors still ship — never returns null.
 */
export const beforeSend = (event /* , hint */) => {
    if (!event || typeof event !== 'object') return event;

    if (event.request && typeof event.request === 'object') {
        if (typeof event.request.url === 'string') {
            event.request.url = scrubUrl(event.request.url);
        }
        if (event.request.headers) {
            scrubHeaders(event.request.headers);
        }
    }

    if (Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs = event.breadcrumbs
            .map((b) => scrubBreadcrumb(b))
            .filter((b) => b !== null);
    }

    return event;
};

/**
 * Sentry `beforeBreadcrumb` hook. Returns null to drop a breadcrumb (per
 * Sentry semantics, this drops the single breadcrumb, not the event).
 */
export const beforeBreadcrumb = (breadcrumb /* , hint */) => {
    return scrubBreadcrumb(breadcrumb);
};
